import { stateCountyData } from '../../../data/stateCountyData.js';
import { isCorrespondent } from '../../../utils/channelUtils.js';
import { impliedPropertyValue } from './rules.js';

export function buildPollyPricingPayload({ form, scenario }) {
    // console.log("Form: ", form, "Scenario: ", scenario);

    const { hidden, visible } = scenario;
    const propertyValue = impliedPropertyValue(form.loanAmount, form.ltv);
    const fico = form.noFico ? 0 : form.fico;

    const now = new Date();
    const lockDays = parseInt(form.lockPeriod) || 30;
    const purchasePrice = propertyValue; // form.loanAmount / (parseFloat(form.ltv || 0) / 100);
    const downPayment = Math.round(purchasePrice - form.loanAmount);
    const purchasePriceRounded = Math.round(purchasePrice);
    const isDSCR = mapIncomeTypeToVerification(form.incomeType, "doc");

    let finalPayload = {
        tenantUTCOffset: -7,
        configurationAsOf: now.toISOString(), // UTC now
        // changesetId: '68768e075361f0f64ba5fac5', // TODO: fetch from Changeset API
        // audienceId: "Champions",
        countyLoanLimitYearToUse: now.getFullYear().toString(),
        encompassTPOConnect: {
            priceGroups: {}
        },
        borrower: {
            firstName: "Ken",
            lastName: "Customer",
            fico: fico,
            income: "14100",
            dtiRatio: form.dti,
            monthsOfReserves: 48,
            isSelfEmployed: false,
            propertiesOwned: 1,
            multipleBorrowerPairs: false,
            isFirstTimeHomeBuyer: false,
            citizenship: mapCitizenship(form.citizenship),
            isITIN: form.citizenship.toString() === "4",
            assetQualificationAmount: null,
            assetDepletionAmount: 0,
            assetUtilizationAmount: 0,
            bankStatementsNumberOfMonthsPersonal: null,
            bankStatementsNumberOfMonthsBusiness: null,
            cpaPandLMonths: null,
            creditGrade: 0,
            debtServiceCoverageRatio: null,
            fullDocMonths: 0,
            investorExperience: "NotApplicable",
            isGiftFunds: false,
            isNonTraditionalCredit: false,
            months1099: 0,
            residualIncome: 0,
            verificationMethod: null,
            verificationOfEmploymentAmount: null,
            dEMonthsBKC7: null,
            dEMonthsBKC11: null,
            dEMonthsBKC13: null,
            dEMonthsChargeOff: null,
            dEMonthsDIL: null,
            dEMonthsForeclosure: mapCreditEvent(form.housingEvent),
            dEMonthsLoanMod: null,
            dEMonthsNoticeOfDefault: null,
            dEMonthsShortSale: null,
            dEMultBK: false,
            dEMultEvents: false,
            latePmt30x12: form.creditSeasoning === "2" ? 1 : 0,
            latePmt30x24: 0,
            latePmt60x12: 0,
            latePmt60x24: 0,
            latePmt90x12: 0,
            latePmt90x24: 0,
            latePmt120x12: 0,
            latePmt120x24: 0,
            isNonOccupancyBorrower: false,
            isNonOccupancyCoborrower: false,
            entityType: "None"
        },
        brokerCompPlan: {
            paidBy: form.brokerCompensation.toString() === "1" ? "Borrower" : "Lender",
            fixedAmount: 0.0,
            percent: form.compPlanRate,
            minAmount: 0.0,
            maxAmount: null,
            calculatedAmount: 0.0,
            calculatedAdjustment: 0.0,
        },
        countyMedianIncomeYearToUse: null,
        loan: {
            losLoanId: null,
            losLoanNumber: null,
            purpose: mapPurpose(form.purpose),
            refinancePurpose: mapRefinancePurpose(form.purpose),
            cashOutAmount: null,
            amount: form.loanAmount,
            purchasePrice: purchasePriceRounded,
            propertyValue: purchasePriceRounded,
            downPayment: downPayment,
            ltv: form.ltv,
            cltv: form.ltv,
            hcltv: form.ltv,
            secondAmount: 0.0,
            helocLineAmount: 0.0,
            helocDrawAmount: 0.0,
            isMortgageInsurancePaidByBorrower: true,
            impounds: form.escrowWaiver ? "None" : "Full",
            rollLenderFee: form.lenderFeeBuyout,
            lenderFee: null,
            aus: "NotSpecified",
            streamlineRefinanceType: null,
            position: mapLienPosition(form.lienPosition),
            applicationDate: null,
            temporaryBuydownType: "None",
            isRelocationLoan: null,
            isSecondCommunityLoan: null,
            isSecondPiggyback: null,
            isSecondInvestorSameAsFirst: null,
            fhaFinanceAmount: null,
            fhaFinancingOption: "Finance",
            fhaMortgageInsurancePremium: null,
            fhaMortgageInsurancePremiumAmount: null,
            fhaTotalLoanAmount: null,
            fhaPriorEndorsementDate: null,
            fhaCaseAssignmentDate: null,
            usdaFinanceAmount: null,
            usdaFinancingOption: "Finance",
            usdaGuaranteeFee: null,
            usdaGuaranteeFeeAmount: null,
            usdaTotalLoanAmount: null,
            prepaymentPenaltyPeriodMonths: 0,
            prepaymentPenaltyStructureType: mapPrepaymentPenalty(form.pppTerm, "structure"),
            prepaymentPenaltyStructure: null,
            vaFinanceAmount: null,
            vaFinancingOption: "Finance",
            vaFundingFeeExempt: null,
            vaDownPayment: null,
            vaDownPaymentAmount: null,
            vaLoanHistory: "First",
            vaFundingFee: null,
            vaFundingFeeAmount: null,
            vaTotalLoanAmount: null,
            vaCashoutLTV: null,
            vaCashoutCLTV: null,
            vaCashoutHCLTV: null,
        },
        property: {
            addressLine1: "",
            addressLine2: null,
            estimatedValue: purchasePriceRounded,
            appraisedValue: purchasePriceRounded,
            state: form.state,
            city: "",
            zipCode: hidden.propertyZip,
            county: "",
            countyFipsCode: null,
            propertyType: mapPropertyType(form.propertyType),
            propertyAttachmentType: hidden.attachmentType,
            occupancy: mapOccupancy(form.occupancy),
            units: form.units,
            stories: 1,
            inspectionWaiver: false,
            isNonWarrantableProject: form.propertyType === "4S",
            isCondotel: form.propertyType === "4D",
            msaCode: null,
            censusTract: null,
            stateFipsCode: "",
            isRuralProperty: false,
            isShortTermRental: false,
            ...stateCountyData.filter(sc => sc.state === form.state).map(({ censusTract, msaCode, ...others }) => others)[0]
        },
        search: {
            desiredLockPeriod: lockDays,
            includeInterestOnlyProducts: form.interestOnly,
            loanTypes: ["NonQM"],
            loanSubTypes: [],
            loanTerms: hidden.term,
            armFixedTerms: hidden.armFixedTerms,
            amortizationTypes: hidden.amortizationTypeInHero,
            customLoanTerm: null,
            productCodes: [],
        },
        customValues: [
            {
                name: "ACH",
                value: form.ach.toString(),
            },
            {
                name: "PPP Term in Months",
                value: mapPrepaymentPenalty(form.pppTerm),
            },
            {
                name: "ADU Count",
                value: "0",
            },
            {
                name: "Asset Depletion",
                value: form.assetDepletion.toString(),
            },
            {
                name: "Asset Utilization Income",
                value: "0",
            },
            {
                name: "Broker YSP",
                value: form.ysp
            },
            {
                name: "C/O Seasoning < 6 Mos",
                value: null,
            },
            {
                name: "CDFI",
                value: form.cdfiEligible.toString(),
            },
            {
                name: "Comp",
                //value: form.compPlanRate,
                value: null,
            },
            {
                name: "Compensation Type",
                value: form.brokerCompensation.toString() === "1" ? "Borrower Paid" : "Lender Paid",
            },
            {
                name: "Debt Service Coverage Ratio",
                value: getDSCRRatioByIncomeType(form.incomeType) ?? null,
            },
            {
                name: "Declining Market",
                value: null,
            },
            {
                name: "Delayed Financing",
                value: null,
            },
            {
                name: "Delegated",
                value: isCorrespondent(form.channel) ? isCorrespondentDel().toString() : null,
            },
            {
                name: "Documentation Type",
                value: mapIncomeTypeToVerification(form.incomeType, "doc"),
            },
            {
                name: "Entity Type",
                value: mapEntityType(form.closingEntity),
            },
            {
                name: "Exception Override",
                value: null,
            },
            {
                name: "Secondary Override",
                value: null,
            },
            {
                name: "Guideline Exception LLPA - Total",
                value: "0",
            },
            {
                name: "Guideline Exception LLRA - Total",
                value: "0",
            },
            {
                name: "HPML",
                value: null,
            },
            {
                name: "Income Verification Type",
                value: mapIncomeTypeToVerification(form.incomeType),
            },
            {
                name: "Investor Experience",
                value: isDSCR && form.rentFree ? "Experienced Investor" : null,
            },
            {
                name: "Last Finished Milestone",
                value: "Setup",
            },
            {
                name: "Living Rent Free",
                value: form.rentFree.toString()
            },
            {
                name: "Location",
                value: null,
            },
            {
                name: "Margin Holdback",
                value: "0",
            },
            {
                name: "Occupant",
                value: null,
            },
            {
                name: "Pre-AD DSCR",
                value: visible.preAssetDepletion ? getPreADDSCR(form.preAssetDepletion) : "0",
            },
            {
                name: "Qualifying Rental Income",
                value: "0"
            },
            {
                name: "Reserves not from Cashout",
                value: "0",
            },
            {
                name: "Rural Property",
                value: null,
            },
            {
                name: "Secondary override",
                value: null,
            },
            {
                name: "Short Term Rental",
                value: visible.shortTermRental ? (form.shortTermRental ? "Yes" : "No") : null,
            },
            {
                name: "Taxes, Insurance, Association Dues",
                value: null,
            },
            {
                name: "Total Qualifying Rental Income",
                value: "0",
            },
            {
                name: "TPO Channel",
                value: isCorrespondent(form.channel) ? "Correspondent" : "Banked - Wholesale",
            },
            {
                name: "TPO Company ID",
                value: null,
            },
            {
                name: "Comp %",
                value: form.compPlanRate,
            },
        ],
        productOverrides: [
            {
                loanType: "NonQM",
                ltv: form.ltv,
                tltv: form.ltv,
            }
        ],
        mortgageInsurance: {},
        adjustments: [],
        productMiDetails: [],
        trimResponse: true,
        sortBy: "price,100"
    };

    return finalPayload;
}

function isCorrespondentDel() {
    const params = new URLSearchParams(window.location.search);
    const del = (params.get('del') ?? '').toLowerCase();
    return (del === '1');
}

const mapCreditEvent = val => {
    switch (val) {
        case "2":
            return 36;
        case "3":
            return 24;
        case "4":
            return 12;
        case "5":
            return 48;
        default:
            return null;
    }
}

const mapCitizenship = val => {
    switch (val) {
        case "0":
            return "USCitizen"; // US Citizen
        case "1":
            return "PermanentResidentAlien"; // Permanent Resident Alien
        case "2":
            return "NonPermanentResidentAlien"; // Non-Permanent Resident Alien
        case "3":
            return "ForeignNational"; // Foreign National
        case "4":
            return "USCitizenAbroad"; // ITIN
        default:
            return "USCitizen"; // Default to US Citizen
    }
}

function mapPurpose(uiValue) {
    const mapping = {
        0: "Purchase", // Purchase => Purchase
        1: "NoCashOutRefinance", // Refi Rate/Term => No Cash-Out Refi
        2: "CashOutRefinance", // Refinance Cashout => Cash-Out Refi
        3: "CashOutRefinance" // Debt Consolidation => Cash-Out Refi
    };
    return mapping[uiValue];
}

function mapRefinancePurpose(uiValue) {
    switch (uiValue) {
        case "1":
            return "NoCashOut";
        case "2":
            return "CashOut";
        case "3":
            return "DebtConsolidation";
        default:
            return null;
    }
}

function mapOccupancy(uiValue) {
    const mapping = {
        0: "PrimaryResidence", // Primary Residence => Primary
        1: "SecondHome", // Secondary Residence => Secondary
        2: "InvestmentProperty" // Investment => Investment
    };
    return mapping[uiValue];
}

function mapPropertyType(value) {
    const mapping = {
        0: "SFR", // SFR => Single Family
        1: "PUD", // PUD => PUD
        2: "Condominium", // Condo => Condo
        MU: "TwoFourUnit", // Multi-Unit => 2-4 Unit
        "4S": "Condominium", // Non-Warrantable Condo => Condo (plus set isNonWarrantableProject = true)
        "4D": "Condominium" // Condotel => Condo (plus set isCondotel = true)
    };
    return mapping[value];
}

const DocType = {
    NONE: 'None',
    FULLDOC: 'Full Doc',
    DSCR: 'DSCR',
    ALTDOC: 'Alt Doc',
};

const getPreADDSCR = val => {
    switch (val) {
        case "0.75-0.99":
            return "0.95"; // 0.75 - 0.99
        case "<0.75":
            return "0.5"; // < 0.75
        default:
            return "0";
    }
};

const getDSCRRatioByIncomeType = incomeType => {
    switch (incomeType) {
        case "700":
        case "800":
            return "1.25"; // DSCR >= 1.25
        case "701":
        case "801":
            return "1.10"; // DSCR 1.0 - 1.24
        case "702":
        case "802":
            return ".80"; // DSCR .75 - .99
        case "703":
            return "0.60"; // DSCR <.75 (No Ratio)
        default:
            return "0";
    }
};

const mapEntityType = entityType => {
    switch (entityType) {
        case "1":
            return "LLC";
        case "2":
            return "Corporation";
        case "3":
            return "Individual";
        default:
            return "Corporation";
    }
};

const mapIncomeTypeToVerification = (text = "", type = "") => {
    switch (text) {
        case "13":
        case "9013":
            return (type === "") ? "1099" : DocType.ALTDOC;
        case "15":
        case "9015":
            return (type === "") ? "Bank Statement - Standard" : DocType.ALTDOC;
        case "15SL":
        case "9015SL":
            return (type === "") ? "Bank Statement - Streamline" : DocType.ALTDOC;
        case "19":
        case "9019":
            return (type === "") ? "Asset Utilization" : DocType.ALTDOC;
        case "17":
        case "9017":
            return (type === "") ? "P&L" : DocType.ALTDOC;
        case "17BS":
        case "9017BS":
            return (type === "") ? "P&L + Bank Statements" : DocType.ALTDOC;
        case "18":
        case "9018":
            return (type === "") ? "Tax Returns" : DocType.FULLDOC;
        case "0":
        case "900":
            return (type === "") ? "W2" : DocType.FULLDOC;
        case "22":
        case "9022":
            return (type === "") ? "WVOE" : DocType.ALTDOC;
        case "6030":
        case "9014":
            return (type === "") ? "Other (retirement, etc)" : DocType.FULLDOC;
        case "21":
            return (type === "") ? "No Income/ No Employment" : DocType.NONE;
        case "20":
        case "700":
        case "701":
        case "702":
        case "703":
        case "800":
        case "801":
        case "802":
            return (type === "") ? "DSCR" : DocType.DSCR;
        case "15S12M":
        case "9015S12M":
            return (type === "") ? "Bank Statement - Standard 12 months" : DocType.ALTDOC;
        case "15S24M":
        case "9015S24M":
            return (type === "") ? "Bank Statement - Standard 24 months" : DocType.ALTDOC;
        default:
            return (type === "") ? "No Selection" : DocType.NONE;
    }
}

const mapLienPosition = (val) => {
    switch (val.toString()) {
        case "1":
            return "First";
        case "2":
            return "Second";
        default:
            return "First";
    }
};

const mapPrepaymentPenalty = (val, type = "months") => {
    let pppMonths;
    let pppType;
    switch (val.toString()) {
        case "0":
            pppMonths = 0;
            pppType = "NoPrepay";
            break;
        case "1":
            pppMonths = 12;
            pppType = "Fixed";
            break;
        case "2":
            pppMonths = 24;
            pppType = "Fixed";
            break;
        case "3":
            pppMonths = 36;
            pppType = "Fixed";
            break;
        case "4":
            pppMonths = 48;
            pppType = "Fixed";
            break;
        case "5":
            pppMonths = 60;
            pppType = "Fixed";
            break;
        case "6":
            pppMonths = 60;
            pppType = "Declining";
            break;
        case "7":
            pppMonths = 36;
            pppType = "Declining";
            break;
        case "8":
            pppMonths = 48;
            pppType = "Declining";
            break;
        case "9":
            pppMonths = 24;
            pppType = "Declining";
            break;
        case "10":
            pppMonths = 6;
            pppType = "Fixed";
            break;
        default:
            pppMonths = 0;
            pppType = "NoPrepay";
            break;
    }

    if (type.toString().toLowerCase() === "months") {
        return pppMonths.toString();
    } else {
        return pppType;
    }
};
